import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  AriaPositionProps,
  DismissButton,
  mergeProps,
  OverlayTriggerProps,
  useDialog,
  useOverlay,
  useOverlayPosition,
  useOverlayTrigger,
} from 'react-aria'
import { useOverlayTriggerState } from 'react-stately'
import { AnimatePresence, motion, useIsPresent } from 'framer-motion'
import { AriaProps } from '../../components/types'
import { useFocusVisible } from '../../components/hooks'
import { useElementScrolledOut } from '../../internal-helpers/useElementScrolledOut'
import { SafeFocusScope } from '../../internal-helpers/SafeFocusScope'
import { useAriaHideOutside } from '../../helpers/useAriaHideOutside'
import { RootPortal } from '../portal/RootPortal'
import { ThemeProviderClean } from '../../../ThemeProviderClean'

interface PublicProps {
  ariaProps?: { type: OverlayTriggerProps['type'] }
  children: React.ReactNode
  crossOffset?: number
  isOpen?: boolean
  offset?: number
  onOpenChange?: (isOpen: boolean) => void
  placement?: AriaPositionProps['placement']
  trigger: (triggerProps: AriaProps) => React.ReactNode
}

interface InternalProps {
  INTERNAL_usePortal?: boolean
}

interface Props extends PublicProps, InternalProps {}

export const PopoverInternal = ({
  ariaProps,
  trigger,
  children,
  isOpen,
  onOpenChange,
  placement,
  offset,
  crossOffset,
  INTERNAL_usePortal = true,
}: Props) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const state = useOverlayTriggerState({ isOpen, onOpenChange })
  const { overlayProps: overlayPositionProps, updatePosition } = useOverlayPosition({
    overlayRef: popoverRef,
    targetRef: triggerRef,
    placement: placement,
    isOpen: state.isOpen,
    offset,
    crossOffset,
  })
  const { triggerProps, overlayProps } = useOverlayTrigger(
    ariaProps ?? { type: 'dialog' },
    {
      ...state,
      close: updatePosition, // by default overlay closes on scroll https://github.com/adobe/react-spectrum/issues/1852#issuecomment-873560234
    },
    triggerRef
  )

  const popoverOverlay = (
    <AnimatePresence>
      {state.isOpen && (
        <ThemeProviderClean>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            <PopoverOverlay
              overlayProps={mergeProps(overlayProps, overlayPositionProps)}
              onClose={state.close}
              ref={popoverRef}
              triggerRef={triggerRef}
            >
              {children}
            </PopoverOverlay>
          </motion.div>
        </ThemeProviderClean>
      )}
    </AnimatePresence>
  )
  return (
    <>
      {trigger({ ...triggerProps, elementRef: triggerRef })}
      {INTERNAL_usePortal ? <RootPortal>{popoverOverlay}</RootPortal> : popoverOverlay}
    </>
  )
}

export const Popover = (props: PublicProps) => <PopoverInternal {...props} />

type PopoverContentProps = {
  children: React.ReactNode
  onClose: () => void
  overlayProps: React.DOMAttributes<HTMLDivElement>
  triggerRef: React.RefObject<HTMLButtonElement | null>
  ref: React.RefObject<HTMLDivElement | null>
}

const PopoverOverlay = ({
  onClose,
  children,
  triggerRef,
  overlayProps,
  ref,
}: PopoverContentProps) => {
  const isPresent = useIsPresent()

  if (!isPresent) {
    // If the popover is animating exit we don't want to prevent clicking outside or trapping focus so we just render the content
    return (
      <PopoverOverlayContent
        isFocusVisible={false}
        ref={ref}
        dialogProps={{}}
        otherProps={overlayProps}
        overlayProps={overlayProps}
        focusProps={{}}
        onClose={onClose}
      >
        {children}
      </PopoverOverlayContent>
    )
  }

  return (
    <PopoverOverlayFull
      onClose={onClose}
      ref={ref}
      triggerRef={triggerRef}
      otherProps={overlayProps}
    >
      {children}
    </PopoverOverlayFull>
  )
}

const PopoverOverlayFull = ({
  children,
  onClose,
  ref,
  triggerRef,
  otherProps,
}: {
  children: React.ReactNode
  onClose: () => void
  ref: React.RefObject<HTMLDivElement | null>
  triggerRef: React.RefObject<HTMLButtonElement | null>
  otherProps: React.DOMAttributes<HTMLDivElement>
}) => {
  const { overlayProps } = useOverlay(
    {
      isOpen: true,
      onClose,
      isDismissable: true,
    },
    ref
  )
  const { dialogProps } = useDialog({}, ref)
  const { focusProps, isFocusVisible } = useFocusVisible()

  useElementScrolledOut(triggerRef, onClose)

  useAriaHideOutside([ref])

  return (
    <SafeFocusScope restoreFocus contain>
      <PopoverOverlayContent
        isFocusVisible={isFocusVisible}
        ref={ref}
        dialogProps={dialogProps}
        otherProps={otherProps}
        overlayProps={overlayProps}
        focusProps={focusProps}
        onClose={onClose}
      >
        {children}
      </PopoverOverlayContent>
    </SafeFocusScope>
  )
}

const PopoverOverlayContent = ({
  isFocusVisible,
  ref,
  dialogProps,
  otherProps,
  overlayProps,
  focusProps,
  children,
  onClose,
}: {
  isFocusVisible: boolean
  ref: React.RefObject<HTMLDivElement | null>
  dialogProps: React.DOMAttributes<HTMLDivElement>
  otherProps: React.DOMAttributes<HTMLDivElement>
  overlayProps: React.DOMAttributes<HTMLDivElement>
  focusProps: React.DOMAttributes<HTMLDivElement>
  children: React.ReactNode
  onClose: () => void
}) => {
  return (
    <>
      <StyledWrapper
        isFocusVisible={isFocusVisible}
        ref={ref}
        {...mergeProps(dialogProps, otherProps, overlayProps, focusProps)}
      >
        {children}
      </StyledWrapper>
      <DismissButton onDismiss={onClose} />
    </>
  )
}

const StyledWrapper = styled.div<{ isFocusVisible: boolean }>`
  ${({ isFocusVisible }) =>
    !isFocusVisible &&
    css`
      :focus {
        outline: none;
      }
    `}
`
